import * as React from "react"
import { RadioGroup, RadioGroupItem } from "@workspace/ui/components/shadcn/radio-group"
import { Label } from "@workspace/ui/components/shadcn/label"
import type { FieldDef, FormApi, FieldOption } from "./fieldTypes"
import { normalizeString, getRequiredMark, getHelpText, getErrorText } from "./fieldUtils"
import { cn } from "@workspace/ui/lib/utils"

type Props = {
    def: Extract<FieldDef, { type: "radio" }>
    form: FormApi
    invalid: boolean
}

function groupClass(def: FieldDef) {
    const layout = def.layout || "row"
    const wrap = def.wrap !== false

    return cn(
        "w-full min-w-0",
        layout === "column" ? "flex flex-col gap-2" : "flex gap-2",
        layout === "row" && wrap && "flex-wrap"
    )
}

function optionClass(invalid: boolean, disabled: boolean) {
    return cn(
        "ui-control",
        "h-9 inline-flex items-center gap-2 px-3 select-none",
        invalid && "ui-control-error",
        !invalid && "ui-control-focus",
        disabled && "ui-control-disabled",
        "min-w-[64px] whitespace-nowrap"
    )
}

export function FieldRadio({ def, form, invalid }: Props) {
    const name = def.name
    const r = form.register(name)
    const value = normalizeString(form.getValue(name))

    const requiredMark = getRequiredMark(def)
    const helpText = getHelpText(def)
    const err = (form.formState.errors as any)?.[name]
    const errorText = getErrorText(err)

    return (
        <>
            <div className="ui-field-header">
                {def.label ? (
                    <Label htmlFor={name} className="ui-field-label">
                        {def.label}
                        {requiredMark}
                    </Label>
                ) : (
                    <span />
                )}
                {helpText ? (
                    <span className="ui-field-helptext">{helpText}</span>
                ) : (
                    <span />
                )}
            </div>

            <RadioGroup
                value={value}
                onValueChange={(v) => r.onChange(v)}
                onBlur={() => r.onBlur()}
                className={groupClass(def)}
            >
                {def.options.map((opt: FieldOption) => (
                    <label
                        key={opt.value}
                        className={optionClass(invalid, !!def.disabled)}
                    >
                        <RadioGroupItem
                            value={opt.value}
                            disabled={def.disabled}
                            className="shrink-0"
                        />
                        <span className="truncate">{opt.label}</span>
                    </label>
                ))}
            </RadioGroup>

            <div className="ui-field-error">
                {errorText || null}
            </div>
        </>
    )
}
